import React, { useRef, useEffect, ReactNode, useState, useMemo, useLayoutEffect, useCallback } from "react";
import * as mapboxgl from "mapbox-gl";
import type { Map } from "mapbox-gl";
import { useBalloonEventsOptions } from "../contexts/EmbedOptions";
import { useCreatorModalSafe } from "../contexts/CreatorModal";
import { createContext, useContext } from "react";
import { useTranslations, useQuery, useApi, useUser, BalloonEventNotifier } from "react-ui";
import { convertBBoxToZoomLocation } from "../utils/mapboxUtils";
import { getEnv } from "../utils/getEnv";
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { useIsMobile } from "react-ui";
import { initCapacitorGeolocation } from "../utils/capacitorGeolocation";
import { useMapInteraction } from "../hooks/useMapInteraction";
import { MapContextMenu } from "./MapContextMenu";
// QuickCreateModal is now rendered by CreatorModalHandler inside EventDataProvider
// Import Mapbox GL CSS
import 'mapbox-gl/dist/mapbox-gl.css';


// Set Mapbox access token — called lazily inside the map init effect
const getMapboxToken = (): string => {
    const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

    if (!token) {
        throw new Error('Mapbox access token is required. Please set VITE_MAPBOX_ACCESS_TOKEN in your environment variables.');
    }

    (mapboxgl as any).accessToken = token;
    return token;
};
interface MapBoxInfo {
    map: Map | null;
}

const MapBoxContext = createContext<MapBoxInfo>({
    map: null,
});

export default function BalloonEventsMapBox({ children }: { children: ReactNode }) {
    const { mapboxStyle, initialLocation, initialZoom, rootId, cooperativeGestures } = useBalloonEventsOptions();
    const containerId = `${rootId}-balloon-events-map-container`
    const mapRef = useRef<Map | null>(null);
    const [mapboxLanguage, setMapboxLanguage] = useState<MapboxLanguage | null>(
        null
    )
    const [map, setMap] = useState<Map | null>(null);
    const [containerReady, setContainerReady] = useState(false);
    // Freeze initial location/zoom at first render so subsequent prop changes
    // don't destroy and recreate the map — location updates use flyTo instead.
    const initialLocationRef = useRef(initialLocation);
    const initialZoomRef = useRef(initialZoom);
    const { addQuery, query, has } = useQuery();
    const isMobile = useIsMobile();
    const { apiEndpoints, apiKey } = useApi();
    const { requiresUser } = useUser();
    const addQueryRef = useRef(addQuery);
    const isMobileRef = useRef(isMobile);

    // Update refs when values change
    useEffect(() => {
        addQueryRef.current = addQuery;
    }, [addQuery]);

    useEffect(() => {
        isMobileRef.current = isMobile;
    }, [isMobile]);

    const { language } = useTranslations();

    /**
     * Adjust the bounding box to exclude the area covered by the sidebar in desktop mode
     * The sidebar is 390px wide on the left side
     */
    const adjustBoundsForSidebar = useCallback((bounds: mapboxgl.LngLatBounds, mapInstance: Map | null, mobile: boolean): mapboxgl.LngLatBounds => {
        if (mobile || !mapInstance) {
            // On mobile, no sidebar covers the map
            return bounds;
        }

        // Get map container dimensions
        const container = mapInstance.getContainer();
        const containerWidth = container.clientWidth;

        // Sidebar width in pixels
        const SIDEBAR_WIDTH = 390;

        // Calculate what percentage of the map width is covered by the sidebar
        const sidebarRatio = SIDEBAR_WIDTH / containerWidth;

        // Get the bounds coordinates
        const west = bounds.getWest();
        const east = bounds.getEast();
        const north = bounds.getNorth();
        const south = bounds.getSouth();

        // Calculate the longitude range
        const lngRange = east - west;

        // Adjust the western boundary to exclude the sidebar area
        // Move it east by the proportion of the sidebar
        const adjustedWest = west + (lngRange * sidebarRatio);

        // Create new adjusted bounds
        return new mapboxgl.LngLatBounds(
            [adjustedWest, south],  // Southwest corner
            [east, north]           // Northeast corner
        );
    }, []);

    function setLanguage(map: Map, language: string) {
        const labels = [
            'continent-label',
            'country-label',
            'state-label',
            'settlement-major-label',
            'settlement-minor-label',
            'settlement-subdivision-label'
        ]

        for (const label of labels) {
            map.setLayoutProperty(label, 'text-field', [
                'get',
                `name_${language}`
            ]);
        }

    }
    //--------------------------------  Language  ----------------------------------
    useEffect(() => {
        if (map && map.isStyleLoaded()) {
            setLanguage(map, language)
        }
    }, [language, map]);

    //--------------------------------  Mapbox Style  ----------------------------------
    useEffect(() => {
        if (map && mapboxStyle) {
            map.setStyle(mapboxStyle);
        }
    }, [mapboxStyle, map]);

    //--------------------------------  Mapbox Events  ----------------------------------
    // We track the last bbox we fired a fetch for and skip the next fire while
    // the new viewport is fully inside it. Reason: every time we refetch, the
    // backend returns the top-N events for the new bbox (sorted by date). If
    // the user zooms in, the backend's "top N for the smaller bbox" can include
    // events that weren't in the previous "top N for the larger bbox", because
    // they didn't rank globally but easily make the cut locally. The frontend
    // supercluster then sees a different input set, and a cluster of 50 at
    // zoom 11 splits into children that DON'T sum to 50 at zoom 13. To prevent
    // that we (a) widen the fetched bbox by 40% so small pans stay inside, and
    // (b) skip the fetch while we're still inside it AND the zoom hasn't
    // changed. The frontend supercluster re-clusters the existing event set
    // instead. Zoom changes MUST refetch because (i) backend clustering
    // depends on zoom, and (ii) an `amount`-bounded set covering a wide bbox
    // may have skipped events that fall inside a smaller, zoomed-in bbox
    // (e.g. tag-filtered langenachtderkirchen Austria-wide vs. zoomed to one
    // village).
    const lastFetchedBboxRef = useRef<mapboxgl.LngLatBounds | null>(null);
    const lastFetchedZoomRef = useRef<number | null>(null);
    useEffect(() => {
        if (!map) return;

        const widenBy = 1.4; // 40% margin (≈ 17% on each side)
        const widen = (b: mapboxgl.LngLatBounds): mapboxgl.LngLatBounds => {
            const sw = b.getSouthWest();
            const ne = b.getNorthEast();
            const dw = ((ne.lng - sw.lng) * (widenBy - 1)) / 2;
            const dh = ((ne.lat - sw.lat) * (widenBy - 1)) / 2;
            return new mapboxgl.LngLatBounds(
                [sw.lng - dw, sw.lat - dh],
                [ne.lng + dw, ne.lat + dh],
            );
        };
        const contains = (outer: mapboxgl.LngLatBounds, inner: mapboxgl.LngLatBounds): boolean => {
            const o_sw = outer.getSouthWest();
            const o_ne = outer.getNorthEast();
            const i_sw = inner.getSouthWest();
            const i_ne = inner.getNorthEast();
            return (
                o_sw.lng <= i_sw.lng && o_sw.lat <= i_sw.lat &&
                o_ne.lng >= i_ne.lng && o_ne.lat >= i_ne.lat
            );
        };

        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        const handler = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const rawBbox = map.getBounds();
                if (!rawBbox) return;
                const adjustedBbox = adjustBoundsForSidebar(rawBbox, map, isMobileRef.current);
                const currentZoom = Math.floor(map.getZoom());

                // Skip refetch only when BOTH the new bbox is inside the
                // previously fetched widened bbox AND the integer zoom level
                // hasn't changed. See block comment above for why zoom
                // changes can't be absorbed by the client-side supercluster.
                const sameZoom = lastFetchedZoomRef.current === currentZoom;
                if (
                    sameZoom
                    && lastFetchedBboxRef.current
                    && contains(lastFetchedBboxRef.current, adjustedBbox)
                ) {
                    return;
                }

                // Fire the search with a widened bbox so subsequent small pans
                // are absorbed by the buffer.
                const fetchBbox = widen(adjustedBbox);
                lastFetchedBboxRef.current = fetchBbox;
                lastFetchedZoomRef.current = currentZoom;

                addQueryRef.current({
                    id: "map",
                    bbox: fetchBbox,
                    zoom: currentZoom,
                });
            }, 250);
        };

        map.on("zoomend", handler)
        map.on("dragend", handler)
        map.on("resize", handler)
        map.on("moveend", handler)
        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            map.off("dragend", handler);
            map.off("zoomend", handler);
            map.off("resize", handler);
            map.off("moveend", handler);
        }
    }, [map]); // Only depend on map instance

    //--------------------------------  Capacitor Geolocation (fire-and-forget)  ----------------------------------
    const geolocationInitialized = useRef(false);
    useEffect(() => {
        if (geolocationInitialized.current) return;
        geolocationInitialized.current = true;
        initCapacitorGeolocation()
            .then((success) => console.log('[MapBox] Geolocation initialized:', success))
            .catch((error) => console.warn('[MapBox] Geolocation init failed:', error));
    }, []);

    //--------------------------------  Fly to new location after map is created  ----------------------------------
    useEffect(() => {
        if (!map || !initialLocation) return;
        // Skip if location is the same object reference as what was used to create the map
        if (initialLocation === initialLocationRef.current) return;
        map.flyTo({
            center: [initialLocation.lng, initialLocation.lat],
            zoom: initialZoom ?? initialZoomRef.current ?? 12,
            duration: 1200,
        });
    }, [initialLocation, initialZoom, map]);

    //--------------------------------  Mapbox GL  ----------------------------------
    useEffect(() => {
        // Skip if map already exists
        if (map) {
            console.log('[MapBox] Map already exists in state');
            return;
        }
        if (mapRef.current) {
            console.log('[MapBox] Map already exists in ref (init in progress)');
            return;
        }

        const container = document.getElementById(containerId);
        if (!container) {
            console.log('[MapBox] Container not found:', containerId);
            return;
        }

        console.log('[MapBox] Container found, initializing map...', {
            containerId,
            containerSize: { width: container.clientWidth, height: container.clientHeight }
        });

        // Check container dimensions - on Android they might be 0 initially
        if (container.clientWidth === 0 || container.clientHeight === 0) {
            console.warn('[MapBox] Container has zero dimensions, scheduling retry...');
            // Schedule a retry after layout completes
            const retryTimeout = setTimeout(() => {
                setContainerReady(prev => !prev); // Toggle to trigger re-render
            }, 100);
            return () => clearTimeout(retryTimeout);
        }

        // Try to use bbox if available, but fallback to defaults if conversion fails
        // Use frozen refs so map creation is not sensitive to prop changes
        let zoom = initialZoomRef.current;
        let center = initialLocationRef.current;

        if (query.bbox) {
            try {
                const result = convertBBoxToZoomLocation(query.bbox, [container.clientWidth, container.clientHeight]);
                zoom = result.zoom;
                center = result.center;
            } catch (error) {
                console.warn('[MapBox] Failed to convert bbox, using defaults:', error);
            }
        }

        const mapboxToken = getMapboxToken();
        let newMap: mapboxgl.Map;
        try {
            newMap = new mapboxgl.Map({
                maxTileCacheSize: 20,
                accessToken: mapboxToken,
                container: containerId,
                style: mapboxStyle,
                center,
                zoom,
                cooperativeGestures
            });
            console.log('[MapBox] Map instance created successfully');
        } catch (error) {
            console.error('[MapBox] Failed to create map instance:', error);
            return;
        }
        // Handle map errors (WebGL failures, etc.)
        newMap.on('error', (e) => {
            console.error('[MapBox] Map error:', e.error?.message || e);
        });

        newMap.addControl(new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true
            },
            trackUserLocation: true,
            showUserHeading: false,
            showAccuracyCircle: false
        }), 'bottom-right');

        mapRef.current = newMap;
        setMap(newMap);

        // Set initial bounds after map loads
        newMap.once('load', () => {
            console.log('[MapBox] Map loaded successfully');
            const rawBbox = newMap.getBounds();
            if (rawBbox) {
                const adjustedBbox = adjustBoundsForSidebar(rawBbox, newMap, isMobileRef.current);
                addQueryRef.current({ id: "map", bbox: adjustedBbox });
            }
        });

        // Fallback: if map doesn't load within 3 seconds, try to get bounds anyway
        // This handles cases where WebGL fails silently on mobile
        const loadTimeout = setTimeout(() => {
            if (!newMap.loaded()) {
                console.warn('[MapBox] Map load timeout - attempting fallback bounds');
                const rawBbox = newMap.getBounds();
                if (rawBbox) {
                    const adjustedBbox = adjustBoundsForSidebar(rawBbox, newMap, isMobileRef.current);
                    addQueryRef.current({ id: "map", bbox: adjustedBbox });
                }
            }
        }, 3000);

        // Cleanup on unmount
        return () => {
            console.log('[MapBox] Cleanup: removing map');
            clearTimeout(loadTimeout);
            newMap.remove();
            mapRef.current = null;
            setMap(null);
        };
        // initialLocation / initialZoom intentionally omitted — location updates use flyTo
        // geolocationReady intentionally omitted — map inits immediately, geolocation is concurrent
    }, [containerId, cooperativeGestures, mapboxStyle, containerReady]);
    //--------------------------------  Resize observer  ----------------------------------
    useLayoutEffect(() => {
        const container = document.getElementById(containerId);
        if (!container) return;
        const observer = new ResizeObserver(() => {
            if (map) {
                map.resize();
            }
        })
        observer.observe(container);
        return () => {
            observer.disconnect();
        }
    }, [map]);

    // Listen for balloon event create/update → fly to event coordinates
    useEffect(() => {
        if (!map) return;
        return BalloonEventNotifier.listen((payload) => {
            if (payload.lng != null && payload.lat != null) {
                console.log('[MapBox] Flying to event:', payload.id, payload.lng, payload.lat);
                map.flyTo({ center: [payload.lng, payload.lat], zoom: 15, duration: 1500 });
            }
        });
    }, [map]);

    // Track whether an external (non-map) bbox was previously active so flyTo
    // only fires when it's intentionally removed, not on every query update.
    const hadExternalBboxRef = useRef(false);
    useEffect(() => {
        if (!mapRef.current) return;

        const externalBbox = !has("map") ? query.bbox : undefined;
        if (externalBbox != null) {
            hadExternalBboxRef.current = true;
            const northEast = "contains" in externalBbox ? externalBbox.getNorthEast() : externalBbox.northEast;
            const southWest = "contains" in externalBbox ? externalBbox.getSouthWest() : externalBbox.southWest;
            mapRef.current.fitBounds([[southWest.lng, southWest.lat], [northEast.lng, northEast.lat]], {
                padding: 0, duration: 500, animate: true,
            });
        } else if (!query.bbox && hadExternalBboxRef.current) {
            // Only reset to default when an external bbox is explicitly cleared
            hadExternalBboxRef.current = false;
            mapRef.current.flyTo({ center: initialLocation, zoom: initialZoom });
        }
    }, [query]);


    const mapInfo = useMemo(() => {
        return { map }
    }, [map])

    // Context menu interaction
    const {
        contextMenuOpen,
        contextMenuPosition,
        contextMenuLocation,
        contextMenuLoading,
        closeContextMenu,
    } = useMapInteraction({
        map,
        isMobile,
        enabled: true, // Can be made configurable via options later
    });

    // Simple Creator modal - use context if available, otherwise local state
    const creatorModal = useCreatorModalSafe();

    const handleCreateEvent = useCallback(() => {
        if (contextMenuLocation && creatorModal) {
            requiresUser({
                callback: () => {
                    creatorModal.openCreator({
                        lng: contextMenuLocation.lng,
                        lat: contextMenuLocation.lat,
                    });
                    closeContextMenu();
                }
            });
        }
    }, [contextMenuLocation, closeContextMenu, requiresUser, creatorModal]);

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "block",
            }}
        >
            <MapBoxContext.Provider value={mapInfo}>
                {children}
            </MapBoxContext.Provider>

            {/* Context Menu */}
            {contextMenuOpen && contextMenuPosition && contextMenuLocation && (
                <MapContextMenu
                    position={contextMenuPosition}
                    location={contextMenuLocation}
                    address={contextMenuLocation.address}
                    loading={contextMenuLoading}
                    onCreateEvent={handleCreateEvent}
                    onClose={closeContextMenu}
                />
            )}

            {/* Quick Create Modal is now rendered by CreatorModalHandler inside EventDataProvider */}
        </div>
    );
}

export function useMapBox(): MapBoxInfo | null {
    const context = useContext(MapBoxContext);
    if (!context) {
        throw new Error("MapBoxContext must be used within a MapBoxProvider");
    }
    return context;
}

function BalloonEventsMapBoxContainer() {
    const { mapScrollable, rootId } = useBalloonEventsOptions();
    return (

        <div
            style={{
                pointerEvents: mapScrollable ? "auto" : "none",
                position: "relative",
                width: "100%",
                height: "100%",
            }}
            id={`${rootId}-balloon-events-map-container`}
        />


    );
}

BalloonEventsMapBox.Container = BalloonEventsMapBoxContainer;
